<template>
  <section class="dashboard">
    <!-- Waiting for the initial session restore -->
    <LoadingSpinnerComponent v-if="!authReady" />

    <!-- Signed-out state -->
    <div v-else-if="!isAuthenticated" class="signed-out lk-card">
      <div class="so-icon">🔗</div>
      <div class="so-title">Sign in to see your dashboard</div>
      <div class="so-sub">Claim and manage your links, files &amp; pastes once you're signed in.</div>
      <router-link class="lk-btn lk-btn-primary" :to="{ name: 'Login', query: { redirect: '/dashboard' } }">Sign in</router-link>
    </div>

    <template v-else>
      <!-- Greeting (mock F1) -->
      <div class="dash-head">
        <div>
          <div class="dash-eyebrow">Welcome back</div>
          <h1 class="dash-title">{{ greeting }}</h1>
        </div>
      </div>

      <!-- Shorten bar (mock F1): white field, terracotta link icon, custom
           slug chip, ink button. -->
      <form class="shorten-bar" @submit.prevent="shorten">
        <svg class="sb-icon" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 17H7A5 5 0 0 1 7 7h2"></path>
          <path d="M15 7h2a5 5 0 1 1 0 10h-2"></path>
          <line x1="8" y1="12" x2="16" y2="12"></line>
        </svg>
        <input v-model="shortenInput" class="sb-input mono" placeholder="Paste a long URL to shorten…" />
        <span class="sb-slug" :class="{ invalid: shortenSlugInvalid }">
          <span class="sb-host mono">{{ shortHost }}/</span>
          <input v-model="shortenSlug" class="sb-slug-input mono" placeholder="custom" maxlength="64" size="7" />
        </span>
        <button class="lk-btn lk-btn-primary sb-btn" :disabled="shortening" type="submit">
          {{ shortening ? 'Working…' : 'Shorten →' }}
        </button>
      </form>
      <div v-if="shortenError" class="shorten-error">{{ shortenError }}</div>
      <div v-if="shortenResult" class="shorten-result">
        <span class="sr-ok">✓</span>
        <span class="mono sr-link">{{ shortDisplay(shortenResult.slug) }}</span>
        <CopyButton :text="shortUrl(shortenResult.slug)" small />
        <router-link class="sr-manage" :to="`/dashboard/link/${shortenResult.slug}`">Manage →</router-link>
        <button class="sr-dismiss" title="Dismiss" @click="shortenResult = null">✕</button>
      </div>

      <!-- Stat row (mock F1: 4-up, one terracotta) -->
      <div class="stat-grid">
        <StatCard label="Active links" :value="links.length" />
        <StatCard label="Pastes" :value="pastes.length" accent />
        <StatCard label="Files" :value="files.length" />
        <StatCard label="Stored" :value="storedValue" :suffix="storedUnit" />
      </div>

      <LoadingSpinnerComponent v-if="loading" />

      <template v-else>
        <!-- ============ YOUR LINKS (mock F1 table) ============ -->
        <div class="table-card">
          <div class="tc-head">
            <div class="tc-title">Your links</div>
            <input v-model="search" class="tc-search mono" placeholder="search…" />
          </div>

          <template v-if="linkRows.length">
            <div class="trow thead links-grid">
              <span>Short link → destination</span>
              <span>Type</span>
              <span>Preview</span>
              <span class="right">Status</span>
            </div>
            <div v-for="r in linkRows" :key="r.slug" class="trow links-grid" @click="openLink(r.slug)">
              <div class="cell-link">
                <span class="mono slug">{{ shortDisplay(r.slug) }}</span>
                <div class="dest">{{ r.destPrefix }} {{ r.destination }}</div>
              </div>
              <span class="mono type" data-label="Type">{{ r.type }}</span>
              <span class="chip" :class="r.previewClass" data-label="Preview">{{ r.preview }}</span>
              <span class="status right" :style="{ color: r.statusColor }" data-label="Status">
                <span class="dot" :style="{ background: r.statusColor }"></span>{{ r.status }}
              </span>
            </div>
          </template>
          <div v-else class="tc-empty">
            <template v-if="search">No links match “{{ search }}”.</template>
            <template v-else>No links yet — shorten your first URL above.</template>
          </div>
        </div>

        <!-- ============ YOUR PASTES (same recipe) ============ -->
        <div class="table-card">
          <div class="tc-head">
            <div class="tc-title">Your pastes</div>
            <router-link class="tc-new" to="/paste">+ New paste</router-link>
          </div>

          <template v-if="pasteRows.length">
            <div class="trow thead pastes-grid">
              <span>Paste</span>
              <span>Syntax</span>
              <span>Size</span>
              <span>Expires</span>
              <span class="right">Status</span>
            </div>
            <div v-for="r in pasteRows" :key="r.paste_id" class="trow pastes-grid" @click="openPaste(r.paste_id)">
              <div class="cell-link">
                <span class="pname">{{ r.title }}</span>
                <div class="dest mono">{{ pasteDisplay(r.paste_id) }}</div>
              </div>
              <span class="mono type" data-label="Syntax">{{ r.language }}</span>
              <span class="mono type" data-label="Size">{{ r.size }}</span>
              <span class="expiry" :style="{ color: r.expiryColor }" data-label="Expires">{{ r.expiry }}</span>
              <span class="status right" :style="{ color: r.statusColor }" data-label="Status">
                <span class="dot" :style="{ background: r.statusColor }"></span>{{ r.statusLabel }}
              </span>
            </div>
          </template>
          <div v-else class="tc-empty">
            <template v-if="search">No pastes match “{{ search }}”.</template>
            <template v-else>No pastes yet — share a snippet of text or code at a short link.</template>
          </div>
        </div>

        <div class="files-note">
          Looking for your files? They live in
          <router-link to="/share">Files</router-link> now.
        </div>
      </template>
    </template>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import LoadingSpinnerComponent from '@/components/LoadingSpinnerComponent.vue';
import StatCard from '@/components/ui/StatCard.vue';
import CopyButton from '@/components/ui/CopyButton.vue';
import { fmtSize, countdown } from '@/utils/format';
import { shortUrl, shortDisplay, shortBase, normalizeUrl, isValidSlug, pasteDisplay } from '@/utils/links';
import { languageLabel } from '@/utils/pasteLanguages';
import authReadyLoad from '@/mixins/authReadyLoad';

function isExpired(x, now) {
  return x.expires ? new Date(x.expires).getTime() <= now : false;
}

export default {
  name: 'ShortLinkView',
  mixins: [authReadyLoad],
  components: { LoadingSpinnerComponent, StatCard, CopyButton },
  data() {
    return {
      search: '',
      shortenInput: '',
      shortenSlug: '',
      shortening: false,
      shortenError: '',
      shortenResult: null,
      now: Date.now(),
      ticker: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('shortLinks', ['stateLinks', 'stateIsLoading']),
    ...mapGetters('files', ['stateFiles']),
    ...mapGetters('pastes', ['statePastes']),
    ...mapState('auth', ['user']),
    loading() { return this.stateIsLoading; },
    links() { return this.stateLinks || []; },
    files() { return this.stateFiles || []; },
    pastes() { return this.statePastes || []; },
    shortHost() { return shortBase().replace(/^https?:\/\//, ''); },
    greeting() {
      const h = new Date().getHours();
      const part = h < 5 ? 'evening' : h < 12 ? 'morning' : h < 18 ? 'afternoon' : 'evening';
      const email = this.user && this.user.email;
      if (!email) return `Good ${part}.`;
      const raw = email.split('@')[0].split(/[._-]+/)[0] || '';
      const name = raw ? raw[0].toUpperCase() + raw.slice(1) : '';
      return name ? `Good ${part}, ${name}.` : `Good ${part}.`;
    },
    shortenSlugInvalid() {
      const slug = (this.shortenSlug || '').trim();
      return !!slug && !isValidSlug(slug);
    },
    storage() { return this.files.reduce((a, f) => a + (f.size_bytes || 0), 0); },
    storedValue() {
      const s = fmtSize(this.storage) || '0 B';
      return s.split(' ')[0];
    },
    storedUnit() {
      const s = fmtSize(this.storage) || '0 B';
      return s.split(' ')[1] || '';
    },
    linkRows() {
      const q = this.search.toLowerCase();
      const fileFor = (id) => this.files.find(x => x.file_id === id);
      const pasteFor = (id) => this.pastes.find(x => x.paste_id === id);
      const rows = this.links.filter(l =>
        l.slug.toLowerCase().includes(q) || (l.redirect_to || l.file_id || '').toLowerCase().includes(q));
      return rows.map(l => {
        let destPrefix = '→';
        let destination = l.redirect_to || '';
        let status = 'active';
        let statusColor = 'var(--success)';
        if (l.type === 'file') {
          const f = fileFor(l.file_id);
          destPrefix = '📎';
          destination = f ? `${f.name} · ${fmtSize(f.size_bytes)}` : (l.file_id || '');
          if (f && f.expires) {
            const expired = isExpired(f, this.now);
            const soon = !expired && new Date(f.expires).getTime() - this.now < 86400000 * 2;
            status = expired ? 'expired' : countdown(new Date(f.expires).getTime(), this.now) + ' left';
            statusColor = expired ? 'var(--faint)' : (soon ? 'var(--warn)' : 'var(--success)');
            if (!expired && !soon) status = 'active';
          }
        } else if (l.type === 'paste') {
          const p = pasteFor(l.paste_id);
          destPrefix = '📋';
          destination = p ? (p.title || 'Untitled paste') : (l.paste_id || '');
        }
        const preview = l.preview_mode || 'destination';
        return {
          slug: l.slug,
          type: l.type,
          destPrefix,
          destination,
          preview,
          previewClass: preview === 'custom' ? 'chip-accent' : (preview === 'masked' ? 'chip-dark' : ''),
          status,
          statusColor,
        };
      });
    },
    pasteRows() {
      const q = this.search.toLowerCase();
      let rows = this.pastes.filter(p =>
        (p.title || '').toLowerCase().includes(q)
        || (p.paste_id || '').toLowerCase().includes(q)
        || (p.slug || '').toLowerCase().includes(q));
      rows = [...rows].sort((a, b) => new Date(b.created) - new Date(a.created));
      return rows.map(p => {
        const expired = isExpired(p, this.now);
        const burned = !!p.burned;
        const soon = !expired && !burned && p.expires && (new Date(p.expires).getTime() - this.now < 86400000);
        return {
          paste_id: p.paste_id,
          slug: p.slug,
          title: p.title || 'Untitled paste',
          language: languageLabel(p.language),
          size: fmtSize(p.size_bytes),
          statusLabel: burned ? 'burned' : (expired ? 'expired' : 'active'),
          statusColor: burned || expired ? 'var(--faint)' : 'var(--success)',
          expiry: burned ? '—' : (expired ? 'expired' : 'in ' + countdown(new Date(p.expires).getTime(), this.now)),
          expiryColor: burned || expired ? 'var(--faint)' : (soon ? 'var(--warn)' : 'var(--muted)'),
        };
      });
    },
  },
  created() {
    // Data loading is driven by the authReadyLoad mixin (loadAuthedData),
    // which fires once the session restore has settled and the user is authed.
    this.ticker = setInterval(() => { this.now = Date.now(); }, 1000);
  },
  beforeUnmount() { clearInterval(this.ticker); },
  methods: {
    ...mapActions('shortLinks', ['getLinks', 'createLink']),
    ...mapActions('files', ['getFiles']),
    ...mapActions('pastes', ['getPastes']),
    loadAuthedData() {
      this.getLinks();
      this.getFiles();
      this.getPastes();
    },
    shortDisplay,
    shortUrl,
    pasteDisplay,
    openLink(slug) { this.$router.push(`/dashboard/link/${slug}`); },
    openPaste(id) { this.$router.push(`/dashboard/paste/${id}`); },
    async shorten() {
      if (this.shortening) return;
      this.shortenError = '';
      this.shortenResult = null;
      const slug = (this.shortenSlug || '').trim();
      if (slug && !isValidSlug(slug)) {
        this.shortenError = 'Custom name may only contain letters, numbers, - and _';
        return;
      }
      const norm = normalizeUrl(this.shortenInput);
      if (!norm.ok) { this.shortenError = norm.error; return; }
      this.shortening = true;
      try {
        const payload = { type: 'redirect', redirectTo: norm.url };
        if (slug) payload.slug = slug;
        this.shortenResult = await this.createLink(payload);
        this.shortenInput = '';
        this.shortenSlug = '';
        this.getLinks();
      } catch (e) {
        // store surfaces a toast on failure (e.g. slug already taken)
      } finally {
        this.shortening = false;
      }
    },
  },
};
</script>

<style scoped>
.dashboard {
  max-width: 1080px;
  margin: 0 auto;
  padding: clamp(24px, 4vw, 40px) clamp(16px, 4vw, 40px) 80px;
}

.dash-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.dash-eyebrow {
  font-family: var(--font-mono);
  font-size: 11px;
  letter-spacing: .16em;
  text-transform: uppercase;
  color: var(--faint);
  margin-bottom: 8px;
}
.dash-title { font-size: clamp(28px, 4.5vw, 40px); line-height: 1; margin: 0; }

.signed-out {
  text-align: center;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.so-icon { font-size: 32px; opacity: .6; }
.so-title { font-size: 16px; font-weight: 700; }
.so-sub { font-size: 13px; color: var(--muted); margin-bottom: 8px; }

/* ---- Shorten bar (mock F1) ---- */
.shorten-bar {
  display: flex;
  align-items: center;
  background: var(--field);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 0 8px 0 16px;
  gap: 12px;
  margin-bottom: 24px;
  box-shadow: var(--shadow);
}
.sb-icon { flex-shrink: 0; color: var(--accent); }
.sb-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 14px;
  padding: 15px 0;
}
.sb-input::placeholder { color: var(--faint); }
.sb-slug {
  display: flex;
  align-items: center;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0 11px;
  transition: border-color .15s;
}
.sb-slug.invalid { border-color: var(--danger); }
.sb-host { color: var(--faint); font-size: 12.5px; white-space: nowrap; }
.sb-slug-input {
  width: 7.5ch;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 12.5px;
  padding: 8px 0;
  transition: width .18s ease;
}
.sb-slug-input::placeholder { color: var(--border-strong); }
.sb-slug-input:focus, .sb-slug-input:not(:placeholder-shown) { width: 13ch; }
.sb-btn { padding: 11px 20px; margin: 8px 0; }

.shorten-error { margin: -14px 0 18px; color: var(--danger); font-size: 12.5px; font-weight: 500; }
.shorten-result {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--card-alt);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 10px 14px;
  margin: -12px 0 22px;
  animation: lk-up .2s ease both;
}
.sr-ok { color: var(--success); font-weight: 700; }
.sr-link { font-size: 13.5px; font-weight: 600; color: var(--accent); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sr-manage { margin-left: auto; font-size: 12.5px; font-weight: 600; color: var(--text); text-decoration: none; border-bottom: 1.5px solid var(--accent); padding-bottom: 1px; white-space: nowrap; }
.sr-dismiss { border: none; background: transparent; color: var(--faint); cursor: pointer; font-size: 13px; padding: 2px 4px; }

/* ---- Stats ---- */
.stat-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-bottom: 26px;
}

/* ---- Editorial table card (mock F1) ---- */
.table-card {
  background: var(--card-alt);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  margin-bottom: 22px;
}
.tc-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px 22px;
  border-bottom: 1px solid var(--border);
}
.tc-title { font-family: var(--font-sans); font-size: 15px; font-weight: 700; color: var(--text); }
.tc-search {
  border: none;
  background: transparent;
  color: var(--muted);
  font-size: 11.5px;
  text-align: right;
  padding: 2px 0;
  width: 120px;
  border-bottom: 1px solid transparent;
  transition: border-color .15s;
}
.tc-search::placeholder { color: var(--faint); }
.tc-search:focus { border-bottom-color: var(--border-strong); }
.tc-new {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--accent-h);
  text-decoration: none;
  font-weight: 600;
}
.tc-new:hover { color: var(--accent); }

.trow {
  display: grid;
  gap: 16px;
  padding: 15px 22px;
  border-bottom: 1px solid var(--code-border);
  align-items: center;
}
.trow:last-child { border-bottom: none; }
.trow:not(.thead) { cursor: pointer; transition: background .12s; }
.trow:not(.thead):hover { background: var(--card); }
.trow.thead {
  padding: 11px 22px;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--faint);
}
.links-grid { grid-template-columns: 2.4fr 0.9fr 1fr 0.9fr; }
.pastes-grid { grid-template-columns: 2.4fr 0.9fr 0.9fr 1fr 0.9fr; }
.right { text-align: right; justify-self: end; }

.cell-link { min-width: 0; }
.slug { font-size: 13.5px; font-weight: 600; color: var(--accent); }
.pname { font-size: 13.5px; font-weight: 600; color: var(--text); }
.dest {
  font-size: 12px;
  color: var(--faint);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}
.type { font-size: 12px; color: var(--muted); }
.expiry { font-size: 12.5px; }

.chip {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--faint);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 2px 7px;
  justify-self: start;
}
.chip-accent { color: var(--accent-h); background: var(--accent-soft); border-color: var(--border-strong); }
.chip-dark { color: var(--muted); background: var(--border); border-color: var(--border-strong); }

.status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; }
.status .dot { width: 7px; height: 7px; border-radius: 50%; }

.tc-empty { padding: 30px 22px; font-size: 13px; color: var(--muted); }

.files-note { font-size: 12.5px; color: var(--faint); }
.files-note a { color: var(--accent-h); font-weight: 600; text-decoration: none; }
.files-note a:hover { text-decoration: underline; }

.mono { font-family: var(--font-mono); }

/* ---- Mobile (mock F1m): stats 2-up, rows become stacked cards ---- */
@media (max-width: 680px) {
  .stat-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
  .shorten-bar { flex-wrap: wrap; padding: 12px 14px; gap: 10px; }
  .sb-input { flex-basis: 70%; padding: 2px 0; }
  .sb-slug { flex: 1; }
  .sb-slug-input { flex: 1; width: auto; }
  .sb-btn { flex-basis: 100%; margin: 0; }

  .table-card { background: transparent; border: none; overflow: visible; }
  .tc-head { padding: 0 2px 11px; border-bottom: none; }
  .tc-title { font-size: 13px; }
  .trow.thead { display: none; }
  .trow {
    grid-template-columns: 1fr auto;
    gap: 4px 10px;
    background: var(--card-alt);
    border: 1px solid var(--border);
    border-radius: 7px;
    padding: 13px 15px;
    margin-bottom: 9px;
  }
  .trow:last-child { border-bottom: 1px solid var(--border); }
  .cell-link { grid-column: 1 / -1; }
  .type, .chip, .expiry { display: none; }
  .status { grid-column: 2; grid-row: 1; }
  .cell-link { grid-row: 1; grid-column: 1; }
  .tc-empty {
    background: var(--card-alt);
    border: 1px solid var(--border);
    border-radius: 7px;
  }
}
</style>
